import { QueryClient } from "@tanstack/react-query";

import { isAbortError, isAuthError, isUnreachableError } from "./errors";

/**
 * TanStack Query defaults for busee.
 *
 * The app is online-first and realtime-backed: PocketBase pushes changes over a
 * subscription, so queries do not need aggressive polling. What they do need is
 * to recover quickly whenever the connection or the window comes back.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Realtime keeps caches warm, so a short staleness window is enough to
        // deduplicate the burst of reads that happens on navigation.
        staleTime: 30_000,
        gcTime: 10 * 60_000,

        // Never let the browser's idea of "online" decide whether to talk to
        // busee. Under TanStack's default `networkMode: "online"` a query whose
        // fetch fails while `navigator.onLine` is false is *paused* rather than
        // failed: it sits at `fetchStatus: "paused"` with no error, so the
        // screen shows a spinner forever instead of "cannot reach the Kobo
        // server". Kobo talks to one self-hosted machine, and whether this
        // device believes it has a route says nothing about whether that
        // machine is answering — a VPN, a captive portal or a LAN-only server
        // all break that assumption in one direction or the other.
        //
        // This is a default for every feature on purpose: an outage has to look
        // the same on all four screens.
        networkMode: "always",

        // Coming back to the app should show current data, not yesterday's.
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,

        retry(failureCount, error) {
          // A rejected session or a cancelled request will never succeed on a
          // retry; anything else is worth two more attempts.
          if (isAuthError(error) || isAbortError(error)) {
            return false;
          }
          return failureCount < 2;
        },
        retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 8_000),
      },
      mutations: {
        // Writes are not idempotent by default — never replay them blindly.
        retry: 0,
        // Same reasoning as the query default above: a write must fail loudly
        // rather than sit paused with the optimistic update still on screen.
        networkMode: "always",
      },
    },
  });
}

/**
 * True when a query failed purely because the server is unreachable.
 *
 * Features use this to render "cannot reach Kobo server" instead of an empty
 * state, which is the difference between "you have nothing to do today" and
 * "we have no idea what you have to do today".
 */
export function isOfflineFailure(error: unknown): boolean {
  return isUnreachableError(error);
}
